import {
  DraggableProvided,
  DraggableProvidedDragHandleProps,
  DroppableProvided,
  DroppableStateSnapshot,
} from "react-beautiful-dnd";
import React, { ReactNode } from "react";

import classNames from "classnames";
import { getDragDropAreaClassName } from "utils";

/**
 * Generates a render function for a Droppable component with react-beautiful-dnd.
 * At the moment, this is purpose built for reports, but could be configurable if needed.
 * It's also OK if this just works for "basic" scenarios and not all possible use cases.
 *
 * See also `draggableRenderFn` below.
 *
 * @example
 *
 * <DragDropContext onDragEnd={handleDragEnd}>
 *   <Droppable droppableId={dropId}>
 *     {droppableRenderFn(items.map((item, i) => <MyItem key={i} item={item} />))}
 *   </Droppable>
 * </DragDropContext>
 */
export const droppableRenderFn = (children: ReactNode | ReactNode[]) => {
  return function dropRenderFn(provided: DroppableProvided, snapshot: DroppableStateSnapshot) {
    return (
      <div
        className={classNames("w-100", getDragDropAreaClassName(snapshot.isDraggingOver))}
        {...provided.droppableProps}
        ref={provided.innerRef}
      >
        {children}
        {provided.placeholder}
      </div>
    );
  };
};

type RenderChildrenProps = {
  dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
};

/**
 * Generates a render function for a Draggable component with react-beautiful-dnd.
 * Provides the dragHandleProps to your render function to forward to a drag handle.
 *
 * See also `droppableRenderFn` above.
 *
 * @example
 *
 * <Draggable key={key} draggableId={dragId} index={i}>
 *   {draggableRenderFn(() => <>...my components here...</>)}
 * </Draggable>
 */
export const draggableRenderFn = (renderChildren: (props: RenderChildrenProps) => ReactNode) => {
  return function dragRenderFn(provided: DraggableProvided) {
    return (
      <div ref={provided.innerRef} {...provided.draggableProps} style={{ ...provided.draggableProps.style }}>
        {renderChildren({ dragHandleProps: provided.dragHandleProps })}
      </div>
    );
  };
};
